'use client';

import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useStats } from '@/hooks/useStats';
import { StatCardSkeleton } from './StatCardSkeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';

function TokenSavedCounterInner() {
  const { stats, isLoading, error } = useStats();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!stats?.tokensSaved) return;

    // Animate from current count to new count
    const start = count;
    const end = stats.tokensSaved;
    const duration = 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      setCount(Math.floor(start + (end - start) * easeOutQuad));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [stats?.tokensSaved, count]);

  // Memoize money calculation to avoid recalculating on every render
  const moneySaved = useMemo(() => {
    return (count * 0.00003).toFixed(2);
  }, [count]);

  if (isLoading) {
    return <StatCardSkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center p-6 sm:p-8 bg-gray-800 rounded-xl border border-gray-700">
        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
          Tokens Saved Today
        </div>
        <div className="text-gray-500 text-sm">
          Unable to load stats
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-6 sm:p-8 bg-gray-800 rounded-xl border border-gray-700">
      <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
        Tokens Saved Today
      </div>
      <motion.div
        key={count}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-blue-400"
      >
        {count.toLocaleString()}
      </motion.div>
      <div className="text-sm text-gray-500 mt-2">
        ≈ ${moneySaved} saved
      </div>
    </div>
  );
}

export function TokenSavedCounter() {
  return (
    <ErrorBoundary
      fallback={
        <div className="flex flex-col items-center p-6 sm:p-8 bg-gray-800 rounded-xl border border-gray-700">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
            Tokens Saved Today
          </div>
          <div className="text-gray-500 text-sm">
            Failed to load stats
          </div>
        </div>
      }
    >
      <TokenSavedCounterInner />
    </ErrorBoundary>
  );
}
